/**
 * EmailAnnotationsBlock — renders the model's draft annotations
 * (Phase X.3.4.5 → X.3.4.6).
 *
 * X.3.4.6 changes vs X.3.4.5:
 *   - Header softened from "Things to resolve before sending" to
 *     "Before you send" (less clinical).
 *   - Conditional label/icon rendering: when annotations.length === 1,
 *     render JUST the warm sentence (no per-item label + icon). When
 *     length > 1, keep the labeled+iconed list (multiple mixed types
 *     need distinguishing).
 *   - Peace warm-cream/gold palette: bg #FBF4E8, left accent #C4A265,
 *     heading #7a5a1f. Differs visually from the sage-cream billing
 *     block below it.
 *
 * Annotation voice is set by the prompt's "How to phrase annotations"
 * section (X.3.4.6 prompt rewrite §2.2): peer-to-peer, "you" addresses
 * the operator, the client is referred to in the third person.
 *
 * Notes pinned to their words: a note with an anchor (the exact words in
 * the email it is about) is a button that focuses the body's textarea and
 * selects the first occurrence of those words, and it shows only while the
 * words are in the body as it reads now, so deleting them hides the note
 * and undoing the edit brings it back (src/lib/annotationAnchors.ts). A
 * note with no anchor always shows. When no note is left, the block,
 * heading included, is gone. Each note carries
 * data-testid="email-annotation-<i>", i its index in the notes given, and
 * data-anchored "true" or "false".
 *
 * Icons are inline hand-rolled SVGs matching the BellIcon / SparkleIcon
 * convention in AppShell.tsx (no icon library in tree). The 'notice'
 * type uses a unicode thought-bubble glyph (X.3.4.7), mirroring the
 * billing block's clock-glyph precedent.
 */

import { type ReactNode, type RefObject } from 'react';
import type { EmailAnnotation, EmailAnnotationType } from '@/lib/api';
import { annotationShows, findAnchorRange, noteAnchor } from '@/lib/annotationAnchors';

export interface EmailAnnotationsBlockProps {
  annotations: readonly EmailAnnotation[];
  /** The email body as it reads now: a note shows while its anchor is in it. */
  body: string;
  /** The body's textarea, where a note's words are selected. */
  bodyRef: RefObject<HTMLTextAreaElement | null>;
  /**
   * False when the body cannot be edited or focused (a finalized saved
   * draft): the notes still show and hide by their words, as plain text.
   */
  selectable?: boolean;
}

const TYPE_LABEL: Record<EmailAnnotationType, string> = {
  verify: 'Verify',
  unclear: 'Unclear',
  notes: 'Notes',
  // X.3.4.7: softened from "Notice" to "Note" (colleague sticky-note
  // register, matching the prompt's annotation voice).
  notice: 'Note',
};

function AnnotationIcon({
  type,
  className,
}: {
  type: EmailAnnotationType;
  className?: string;
}): ReactNode {
  // 24x24 outline; currentColor inherits from parent. Convention matches
  // AppShell.tsx:BellIcon + SparkleIcon.
  switch (type) {
    case 'verify':
    case 'unclear':
      // Triangle exclamation
      return (
        <svg
          className={className}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'notes':
      // Notepad
      return (
        <svg
          className={className}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="9" y1="13" x2="15" y2="13" />
          <line x1="9" y1="17" x2="15" y2="17" />
        </svg>
      );
    case 'notice':
    default:
      // X.3.4.7: thought-bubble glyph (colleague-note register) rendered
      // the same way the billing heading renders its clock glyph
      // (EmailBillingBlock.tsx). Replaces the prior info-circle SVG.
      return (
        <span
          className={className}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          aria-hidden="true"
        >
          💭
        </span>
      );
  }
}

/** The notes that show against the body, each with its index in the notes given. */
export function visibleAnnotations(
  annotations: readonly EmailAnnotation[],
  body: string,
): Array<{ note: EmailAnnotation; index: number }> {
  return annotations
    .map((note, index) => ({ note, index }))
    .filter(({ note }) => annotationShows(note, body));
}

const NOTE_BUTTON_CLS =
  'w-full text-left rounded-md underline decoration-dotted underline-offset-2 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

export function EmailAnnotationsBlock({
  annotations,
  body,
  bodyRef,
  selectable = true,
}: EmailAnnotationsBlockProps): ReactNode {
  const shown = visibleAnnotations(annotations, body);
  if (shown.length === 0) return null;

  // Focus the body and select the first occurrence of the note's words, in
  // the textarea's own text (which is the body as it reads now).
  const selectWords = (anchor: string): void => {
    const el = bodyRef.current;
    if (!el) return;
    const range = findAnchorRange(el.value, anchor);
    if (!range) return;
    // Select, then focus, so the browser scrolls the selection into view.
    el.blur();
    el.setSelectionRange(range.start, range.end);
    el.focus();
  };

  // X.3.4.6: warm-cream/gold ground (different from the sage-cream
  // billing block below) so a CM can visually distinguish the two
  // surfaces at a glance.
  const containerStyle = {
    backgroundColor: '#FBF4E8',
    borderLeftColor: 'var(--color-warm-400)',
  } as const;
  const headingStyle = { color: 'var(--color-caution-deep)' } as const;
  const bodyStyle = { color: 'var(--color-ink)' } as const;

  const isSingle = shown.length === 1;

  return (
    <section
      className="rounded-lg border-l-4 border-y border-r border-sage-800/[8%] p-4 space-y-2"
      style={containerStyle}
      data-testid="email-annotations-block"
      aria-label="Annotations"
    >
      <h3 className="text-sm font-semibold" style={headingStyle}>
        Before you send
      </h3>
      {isSingle ? (
        // Single-annotation shape: a single warm sentence reads as a
        // colleague note (per the prompt's annotation voice). Label
        // and icon add unnecessary register weight in this case.
        <div className="text-sm leading-relaxed" style={bodyStyle} data-testid="email-annotation-single">
          <NoteText note={shown[0]!.note} index={shown[0]!.index} onSelect={selectable ? selectWords : null}>
            {shown[0]!.note.text}
          </NoteText>
        </div>
      ) : (
        <ul className="space-y-2 list-none pl-0">
          {shown.map(({ note: a, index: i }) => (
            <li key={i} className="text-sm" style={bodyStyle}>
              <NoteText note={a} index={i} onSelect={selectable ? selectWords : null}>
                <span className="flex items-start gap-2">
                  <AnnotationIcon type={a.type} className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <span className="font-semibold mr-1" style={headingStyle}>
                      {TYPE_LABEL[a.type]}:
                    </span>
                    <span>{a.text}</span>
                  </span>
                </span>
              </NoteText>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One note: a button that selects its words when it has an anchor, plain
 * text otherwise. The button's accessible name is the note's own text.
 */
function NoteText({
  note,
  index,
  onSelect,
  children,
}: {
  note: EmailAnnotation;
  index: number;
  onSelect: ((anchor: string) => void) | null;
  children: ReactNode;
}): ReactNode {
  const anchor = noteAnchor(note);
  if (anchor === null || onSelect === null) {
    return (
      <div data-testid={`email-annotation-${index}`} data-anchored={anchor === null ? 'false' : 'true'}>
        {children}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={NOTE_BUTTON_CLS}
      data-testid={`email-annotation-${index}`}
      data-anchored="true"
      onClick={() => onSelect(anchor)}
    >
      {children}
    </button>
  );
}
